import React, { useContext } from 'react'
import type { Genre } from '../../../grab/types'
import type { StaticEvent, StaticGroup, DetailLayoutConfig, DetailElementConfig } from '../types'
import { MainConfig, MainData, PageScheduleConfig, useLang } from '../chapito-static'
import { MainStylesVars, StyleSheet } from '../stylesheet'
import { BadgeList, Card, SocialsList, IconText, HorizontalBar, NavigationLink } from './components-lib'
import { GuestOrEventCard } from './guest-or-event-card'
import { ResourceImage } from './resource-image'
import { formatDayLong, formatEndAndStartHour, nowMs, showHour } from '../../../services/date'
import { groupTagsByCategory } from '../../../services/tags'
import { SpotifyEmbed } from './spotify-embed'
import { Title } from './title'
import { YoutubeEmbed } from './youtube-embed'
import { Collapse, ConfigProvider } from 'antd'
import { faUser, faPhone, faEnvelope, faMarker } from '@fortawesome/free-solid-svg-icons'
import { isNotNullish, keys } from '../../../services/typescript'
import { useTranslation } from 'react-i18next'

// Returns true for #rrggbb00 — the only hex form that means "fully see-through"
// in the BO ColorPicker (hexa mode). Other lengths/formats are treated as
// non-transparent.
function isFullyTransparent(color: string): boolean {
  return /^#[0-9a-f]{6}00$/i.test(color)
}

type DetailLayoutProps = {
  layout: DetailLayoutConfig
  guest?: StaticGroup
  event?: StaticEvent
}

export const DetailLayout: React.FC<DetailLayoutProps> = ({ layout, guest, event }) => {
  const lang = useLang()
  const { t } = useTranslation()
  const config = useContext(MainConfig)
  const schedule = useContext(PageScheduleConfig)
  const data = useContext(MainData)
  const dateFormatConfig = { lang, timezone: config.timezone }

  // Resolve a translatable section title to a string for the current language
  // Returns undefined if title is not set, empty string if explicitly emptied
  const resolveTitle = (title?: Record<string, string>): string | undefined => {
    if (!title) return undefined
    const resolved = title[lang] || title['fr']
    if (resolved) return resolved
    // Title object exists but all values are empty → return '' (explicitly no title)
    return Object.values(title).every((v) => !v) ? '' : undefined
  }

  // Render a single element based on its configuration
  const renderElement = (elementConfig: DetailElementConfig, index: number): React.ReactNode => {
    const { type, font, backgroundColor, tagCategoryName, sectionTitle: rawSectionTitle } = elementConfig
    const sectionTitle = resolveTitle(rawSectionTitle)

    // DESCRIPTION defaults to the theme block colors (text on block bg),
    // all other elements default to white (text on image).
    const isDescription = type === 'DESCRIPTION'
    const defaultFontColor = isDescription ? MainStylesVars.colorBlockText : '#ffffff'
    const defaultBgColor = isDescription ? MainStylesVars.colorBlockBackground : undefined
    const resolvedBg = backgroundColor ?? defaultBgColor
    const hasBackground = !!resolvedBg && !isFullyTransparent(resolvedBg)
    const elementStyle: React.CSSProperties = {
      color: font?.color ?? defaultFontColor,
      fontSize: font?.size ? `${font.size}px` : undefined,
      fontFamily: font?.family,
      fontWeight: font?.weight,
      backgroundColor: hasBackground ? resolvedBg : undefined,
      padding: hasBackground ? '12px' : undefined,
      borderRadius: hasBackground ? '8px' : undefined,
      marginBottom: '12px',
    }

    switch (type) {
      case 'NAME': {
        const name = guest?.name ?? event?.title
        return name ? (
          <div key={index} style={{ ...elementStyle, fontWeight: 'bold', fontSize: font?.size ? `${font.size}px` : '1.5em' }}>
            {name}
          </div>
        ) : null
      }

      case 'DESCRIPTION': {
        const description = guest?.description ?? event?.description
        return description ? (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </div>
        ) : null
      }

      case 'IMAGE': {
        const image = guest?.image ?? event?.image
        return image ? (
          <div key={index} style={{ marginBottom: '12px' }}>
            <ResourceImage imageId={image} style={styles.image} alt="" />
          </div>
        ) : null
      }

      case 'TAGS': {
        let genres: Genre[] = guest?.genres ?? event?.genres ?? []
        if (tagCategoryName && genres.length > 0) {
          genres = genres.filter((g) => g.category?.name === tagCategoryName)
        }
        if (genres.length === 0) return null

        const groupedTags = groupTagsByCategory(genres, { lang })
        // sectionTitle undefined = use default category name, sectionTitle empty string = no title
        const resolvedTitle = rawSectionTitle === undefined ? undefined : sectionTitle
        return (
          <div key={index} style={elementStyle}>
            {groupedTags.map(({ category, genres: categoryGenres, key }) => (
              <div key={key} style={styles.tagsContainer}>
                {(resolvedTitle ?? category.name) && <div style={styles.tagsCategoryTitle}>{resolvedTitle ?? category.name}</div>}
                <BadgeList labels={categoryGenres.map((g) => g.name)} />
              </div>
            ))}
          </div>
        )
      }

      case 'TIME': {
        if (!event) return null
        const timeStr = showHour(event.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(event, dateFormatConfig, schedule) : ''
        return timeStr ? (
          <div key={index} style={elementStyle}>
            {timeStr}
          </div>
        ) : null
      }

      case 'DATE_TIME': {
        if (!event) return null
        const dateStr = formatDayLong(event.day, dateFormatConfig)
        const timeStr = showHour(event.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(event, dateFormatConfig, schedule) : ''
        return (
          <div key={index} style={elementStyle}>
            <span style={{ textTransform: 'capitalize' }}>{dateStr}</span>
            {timeStr && <span> - {timeStr}</span>}
          </div>
        )
      }

      case 'SCENE': {
        if (!event?.scene || event.hideLocation || config.eventsHideLocation) return null
        return (
          <div key={index} style={elementStyle}>
            {event.scene.name}
          </div>
        )
      }

      case 'SPOTIFY': {
        // For guests, use the guest's spotify link directly
        // For events, find the first linked guest that has a spotify link
        const spotifyLink = guest?.links?.spotify ?? event?.musicGroups?.find((g) => g.links?.spotify)?.links?.spotify
        if (!spotifyLink) return null
        return (
          <div key={index} style={{ marginBottom: '12px' }}>
            <SpotifyEmbed spotifyArtistLink={spotifyLink} />
          </div>
        )
      }

      case 'GUESTS': {
        // Only for events - display the list of linked guests
        const guests = event?.musicGroups ?? []
        if (guests.length === 0) return null

        // Group guests by type
        const guestsByType = guests.reduce<Record<string, StaticGroup[]>>((acc, g) => {
          const typeName = g.guestType?.name ?? 'Artistes'
          acc[typeName] = [...(acc[typeName] || []), g]
          return acc
        }, {})

        const guestSection = (g: StaticGroup): { label: React.ReactNode; children: React.ReactNode } => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'center', color: MainStylesVars.colorBlockAction }}>
              {g.image && <ResourceImage imageId={g.image} alt="" style={{ maxHeight: 30, marginRight: 10 }} />}
              <div>{g.name}</div>
              {g.location && (
                <>
                  &nbsp;<div>{g.location}</div>
                </>
              )}
            </div>
          ),
          children: (
            <div style={styles.guestContent}>
              {g.description && (
                <Card>
                  <div dangerouslySetInnerHTML={{ __html: g.description }} />
                </Card>
              )}
              {g.links && <SocialsList socials={g.links} />}
            </div>
          ),
        })

        return (
          <div key={index} style={elementStyle}>
            {Object.entries(guestsByType).map(([typeName, typeGuests]) => (
              <div key={typeName}>
                <Title level={3}>{sectionTitle ?? typeName}</Title>
                <ConfigProvider
                  theme={{
                    token: {
                      colorTextBase: config.colorBlockText,
                      colorBgContainer: config.colorBackground,
                      colorBorder: config.colorBackground,
                    },
                  }}
                >
                  <Collapse
                    items={typeGuests.map((g, i) => ({ ...guestSection(g), key: i }))}
                    bordered={true}
                    style={{ backgroundColor: config.colorBlockBackground }}
                  />
                </ConfigProvider>
              </div>
            ))}
          </div>
        )
      }

      case 'COORDS': {
        // For guests, use the guest's contact info directly
        // For events, use the first linked guest's contact info
        const contactSource = guest ?? event?.musicGroups?.[0]
        const { email, phone, address, contact } = contactSource ?? {}

        if (!email && !phone && !address && !contact) return null

        const coordsItems = [
          contact ? <IconText key="contact" icon={faUser} value={contact} /> : null,
          phone ? <IconText key="phone" icon={faPhone} href={`tel:${phone}`} value={phone} /> : null,
          email ? <IconText key="email" icon={faEnvelope} href={`mailto:${email}`} value={email} /> : null,
          address ? <IconText key="address" icon={faMarker} value={address} /> : null,
        ].filter(isNotNullish)

        return (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            <Card style={styles.coordsCard}>
              {coordsItems.reduce<React.ReactNode>(
                (acc, item, i) => (
                  <>
                    {acc}
                    {i !== 0 && <HorizontalBar style={{ marginTop: 3, marginBottom: 3, marginLeft: 40 }} />}
                    {item}
                  </>
                ),
                null
              )}
            </Card>
          </div>
        )
      }

      case 'GUEST_TAGS': {
        if (!event) return null
        // Collect all genres from linked guests, deduplicate by _id
        const seenIds = new Set<string>()
        const guestGenres: Genre[] = []
        for (const g of event.musicGroups ?? []) {
          for (const genre of g.genres) {
            if (!seenIds.has(genre._id)) {
              seenIds.add(genre._id)
              guestGenres.push(genre)
            }
          }
        }
        // Filter by tag category if specified
        const filteredGuestGenres = tagCategoryName ? guestGenres.filter((g) => g.category?.name === tagCategoryName) : guestGenres
        if (filteredGuestGenres.length === 0) return null

        const groupedGuestTags = groupTagsByCategory(filteredGuestGenres, { lang })
        const resolvedGuestTagTitle = rawSectionTitle === undefined ? undefined : sectionTitle
        return (
          <div key={index} style={elementStyle}>
            {groupedGuestTags.map(({ category, genres: categoryGenres, key }) => (
              <div key={key} style={styles.tagsContainer}>
                {(resolvedGuestTagTitle ?? category.name) && <div style={styles.tagsCategoryTitle}>{resolvedGuestTagTitle ?? category.name}</div>}
                <BadgeList labels={categoryGenres.map((g) => g.name)} />
              </div>
            ))}
          </div>
        )
      }

      case 'RELATED_EVENTS': {
        // For guests: other events they appear in
        // For events: other events that share the same guests
        const eventGroupIds = new Set(event?.musicGroups?.map((g) => g._id) ?? [])
        const relatedEvents: StaticEvent[] = guest
          ? guest.relatedEvents
          : event
          ? data.events.filter((e) => e.id !== event.id && e.musicGroups.some((g) => eventGroupIds.has(g._id)))
          : []

        if (relatedEvents.length === 0) return null

        const relatedTitle = sectionTitle ?? t('ARTIST.OTHER_EVENTS')

        return (
          <div key={index} style={elementStyle}>
            {relatedTitle && <Title level={3}>{relatedTitle}</Title>}
            <div style={styles.similarEventsGrid}>
              {relatedEvents.map((ev) => (
                <NavigationLink key={ev.id} path={{ page: 'event', eventId: ev.id }} style={{ flex: '1 1 240px', minWidth: 0, maxWidth: '100%' }}>
                  <GuestOrEventCard groupOrEvent={ev} event={ev} display="small" style={{ height: '100%' }} />
                </NavigationLink>
              ))}
            </div>
          </div>
        )
      }

      case 'SIMILAR_EVENTS': {
        if (!event) return null
        const currentGenreIds = new Set(event.genres.map((g) => g._id))
        if (currentGenreIds.size === 0) return null

        // Score each other event by number of common genres
        const scored = data.events
          .filter((e) => e.id !== event.id)
          .map((e) => ({
            event: e,
            commonCount: e.genres.filter((g) => currentGenreIds.has(g._id)).length,
          }))
          .filter((s) => s.commonCount > 0)

        if (scored.length === 0) return null

        // Sort by commonCount DESC, shuffle ties randomly
        scored.sort((a, b) => {
          if (b.commonCount !== a.commonCount) return b.commonCount - a.commonCount
          return Math.random() - 0.5
        })

        const similarEvents = scored.slice(0, 3).map((s) => s.event)

        return (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            <div style={styles.similarEventsGrid}>
              {similarEvents.map((ev) => (
                <NavigationLink key={ev.id} path={{ page: 'event', eventId: ev.id }} style={{ flex: '1 1 240px', minWidth: 0, maxWidth: '100%' }}>
                  <GuestOrEventCard groupOrEvent={ev} event={ev} display="small" style={{ height: '100%' }} />
                </NavigationLink>
              ))}
            </div>
          </div>
        )
      }

      case 'VIDEO': {
        const videoKey = guest?.links?.youtubeVideoKey
        if (!videoKey) return null
        return (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            <YoutubeEmbed videoId={videoKey} />
          </div>
        )
      }

      case 'SOCIALS': {
        const links = guest?.links
        if (!links) return null
        const hasAnyLink = keys(links).some((k) => k !== 'youtubeVideoKey' && !!links[k])
        if (!hasAnyLink) return null
        return (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            <SocialsList socials={links} />
          </div>
        )
      }

      case 'COPYRIGHT': {
        const copyright = guest?.copyright ?? event?.copyright
        if (!copyright) return null
        return (
          <div key={index} style={{ ...elementStyle, fontStyle: 'italic', opacity: 0.8 }}>
            {t('ARTIST.COPYRIGHT')} @ {copyright}
          </div>
        )
      }

      case 'CONTACT': {
        const contact = guest?.contact ?? event?.musicGroups?.[0]?.contact
        if (!contact) return null
        return (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            {contact}
          </div>
        )
      }

      case 'GUEST_TYPE': {
        const guestTypeName = guest?.guestType?.name ?? event?.musicGroups?.[0]?.guestType?.name
        if (!guestTypeName) return null
        return (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            {guestTypeName}
          </div>
        )
      }

      case 'NEXT_EVENT_DATE_TIME':
      case 'NEXT_EVENT_DATE':
      case 'NEXT_EVENT_SCENE': {
        if (!guest) return null
        const nextEvt = guest.relatedEvents?.filter((e) => e.showStartDate > nowMs()).sort((a, b) => a.showStartDate - b.showStartDate)[0]
        if (!nextEvt) return null

        let value: string | null = null
        if (type === 'NEXT_EVENT_DATE_TIME') {
          const dateStr = formatDayLong(nextEvt.day, dateFormatConfig)
          const timeStr = showHour(nextEvt.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(nextEvt, dateFormatConfig, schedule) : ''
          value = `${dateStr}${timeStr ? ` ${timeStr}` : ''}`
        } else if (type === 'NEXT_EVENT_DATE') {
          value = formatDayLong(nextEvt.day, dateFormatConfig)
        } else if (type === 'NEXT_EVENT_SCENE') {
          value = nextEvt.scene?.name && !nextEvt.hideLocation && !config.eventsHideLocation ? nextEvt.scene.name : null
        }

        if (!value) return null
        return (
          <div key={index} style={elementStyle}>
            {sectionTitle && <Title level={3}>{sectionTitle}</Title>}
            <span style={{ textTransform: 'capitalize' }}>{value}</span>
          </div>
        )
      }

      default:
        return null
    }
  }

  // Calculate active columns (non-empty or with width > 0)
  const activeColumns = layout.columns.filter((col) => col.elements.length > 0 || col.widthPercent > 0)

  return (
    <div style={styles.container}>
      {/* Banner */}
      {layout.banner?.element && (
        <div
          style={{
            ...styles.banner,
            backgroundColor:
              layout.banner.backgroundColor && !isFullyTransparent(layout.banner.backgroundColor) ? layout.banner.backgroundColor : undefined,
          }}
        >
          {renderElement(layout.banner.element, 0)}
        </div>
      )}

      {/* Columns */}
      {activeColumns.length > 0 && (
        <div style={styles.columnsContainer}>
          {activeColumns.map((column, colIndex) => (
            <div
              key={colIndex}
              style={{
                ...styles.column,
                flex: column.widthPercent > 0 ? `0 0 ${column.widthPercent}%` : '1',
                maxWidth: column.widthPercent > 0 ? `${column.widthPercent}%` : undefined,
              }}
            >
              {column.elements.map((element, elemIndex) => renderElement(element, elemIndex))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },

  banner: {
    padding: 20,
    borderRadius: 10,
  },

  columnsContainer: {
    display: 'flex',
    gap: 20,
    flexWrap: 'nowrap',
  },

  column: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },

  image: {
    width: '100%',
    borderRadius: 10,
  },

  tagsContainer: {
    marginBottom: 15,
  },

  tagsCategoryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    paddingBottom: 8,
    color: MainStylesVars.colorBlockAction,
  },

  guestContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  coordsCard: {
    marginTop: 12,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 15,
    paddingRight: 15,
  },

  similarEventsGrid: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
})
